import { App, Form, Spin } from "antd";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { CustomActionButtons, CustomModal } from "sipli-fleet-core/components";
import { CrossIcon } from "src/assets/svgs";
import CustomerLimitGuardAlert from "src/container/Customers/CustomerDetail/CustomerLimitGuardAlert";
import { useCustomerLimitGuard } from "src/container/Customers/CustomerDetail/useCustomerLimitGuard";
import {
  useMutationCreateUser,
  useMutationSaveAllowedSystem,
  useMutationSaveUserActivities,
  useMutationSaveUserTimedData,
  useMutationSaveUserVehicles,
  useMutationUpdateUser,
  useQueryGetUserById,
  useQueryGetUserRoleLookup,
} from "src/queries/users.queries";
import type { User } from "src/services/userService";
import { useSelectDistributor } from "src/store/selectDistributorStore";
import LimitationsStep, { type LimitationsState } from "./LimitationsStep";
import SystemPermissionsStep from "./SystemPermissionsStep";
import UserInfoStep from "./UserInfoStep";
import type {
  PermissionsBySystemId,
  UserFormValues,
  UserModalStep,
  UserRole,
  UserSystem,
} from "./types";
import {
  buildInitialFormValues,
  buildUserActivitiesPayload,
  buildUserPayload,
  getCurrentRole,
  getResponseArray,
  getResponseObject,
  getRoleName,
  getUserId,
  isCustomerRoleValue,
  isCustomerUserRoleValue,
  isSuperAdminRoleValue,
  isSuperUserRoleValue,
  normalizeUserDetailResponse,
  shouldRequirePassword,
} from "./userModalHelpers";
import "./AddEditUserModal.css";

const CrossIconComponent = CrossIcon as unknown as ComponentType<{
  className?: string;
}>;

interface AddEditUserModalProps {
  open: boolean;
  record?: User | null;
  onClose: () => void;
}

const steps = ["user_info", "system_permissions", "limitations"] as const;
type StepKey = (typeof steps)[number];

const defaultLimitations: LimitationsState = {
  historicalEnabled: false,
  selectedPeriod: undefined,
  monthsAgo: undefined,
  selectedVehicles: [],
};

const StepHeader = ({
  current,
  maxReachableStep,
  visibleSteps,
  onStepClick,
  onClose,
}: {
  current: UserModalStep;
  maxReachableStep: UserModalStep;
  visibleSteps: readonly StepKey[];
  onStepClick: (step: UserModalStep) => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <div className="user-modal-stepper">
      <div className="user-modal-stepper-items">
        {visibleSteps.map((step, index) => {
          const disabled = index > maxReachableStep;

          return (
            <div className="user-modal-stepper-item-wrap" key={step}>
              <button
                type="button"
                className={`user-modal-stepper-item ${current === index ? "active" : ""}`}
                disabled={disabled}
                onClick={() => {
                  if (!disabled) onStepClick(index as UserModalStep);
                }}
              >
                <span>{index + 1}</span>
                <strong>{t(step)}</strong>
              </button>
              {index < visibleSteps.length - 1 ? (
                <span className="user-modal-stepper-line" />
              ) : null}
            </div>
          );
        })}
      </div>
      <button type="button" className="user-modal-close" aria-label={t("close")} onClick={onClose}>
        <CrossIconComponent className="h-[14px] w-[14px]" />
      </button>
    </div>
  );
};

const AddEditUserModal = ({ open, record, onClose }: AddEditUserModalProps) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<UserFormValues>();
  const recordUserId = getUserId(record);
  const initialEditMode = Boolean(recordUserId);
  const hydratedKeyRef = useRef<string | undefined>(undefined);
  const [step, setStep] = useState<UserModalStep>(0);
  const [maxReachableStep, setMaxReachableStep] = useState<UserModalStep>(0);
  const [savedUser, setSavedUser] = useState<User | null>(record ?? null);
  const [permissionsBySystemId, setPermissionsBySystemId] = useState<PermissionsBySystemId>({});
  const [permissionSystems, setPermissionSystems] = useState<UserSystem[]>([]);
  const [selectedSystemId, setSelectedSystemId] = useState<string | number>();
  const [enabledSystemIds, setEnabledSystemIds] = useState<Set<string | number>>(() => new Set());
  const [dirtyEnabledSystemIds, setDirtyEnabledSystemIds] = useState<Set<string>>(() => new Set());
  const [dirtyPermissionSystemIds, setDirtyPermissionSystemIds] = useState<Set<string>>(
    () => new Set()
  );
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(false);
  const [limitations, setLimitations] = useState<LimitationsState>(defaultLimitations);
  const { selectedDistributor } = useSelectDistributor();
  const currentRole = getCurrentRole();

  const userDetailQuery = useQueryGetUserById(recordUserId, {
    enabled: open && initialEditMode,
  });

  const rolesQuery = useQueryGetUserRoleLookup({ enabled: open });

  const createUserMutation = useMutationCreateUser();
  const updateUserMutation = useMutationUpdateUser();
  const saveAllowedSystemMutation = useMutationSaveAllowedSystem();
  const saveActivitiesMutation = useMutationSaveUserActivities();
  const saveVehiclesMutation = useMutationSaveUserVehicles();
  const saveTimedDataMutation = useMutationSaveUserTimedData();

  const userDetailLoaded = !initialEditMode || userDetailQuery.isFetched || userDetailQuery.isError;
  const fetchedUserDetail = normalizeUserDetailResponse(userDetailQuery.data);
  const userDetail =
    fetchedUserDetail ?? (userDetailLoaded && record ? { user: record } : undefined);
  const hydratedRecord = userDetail?.user ?? null;

  const roles = useMemo(() => getResponseArray<UserRole>(rolesQuery.data), [rolesQuery.data]);
  const activeUserId = getUserId(savedUser) ?? getUserId(hydratedRecord);
  const isEditing = Boolean(activeUserId);
  const watchedRoleId = Form.useWatch("userRoleId", form);
  const watchedClientId = Form.useWatch("clientId", form);
  const selectedRoleId =
    watchedRoleId ??
    form.getFieldValue("userRoleId") ??
    savedUser?.userRole?.id ??
    hydratedRecord?.userRole?.id;
  const savedUserRoleId = savedUser?.userRole?.id ?? hydratedRecord?.userRole?.id;
  const selectedRoleName =
    getRoleName(roles, selectedRoleId) ??
    (selectedRoleId !== undefined && String(selectedRoleId) === String(savedUserRoleId)
      ? (savedUser?.userRole?.name ?? hydratedRecord?.userRole?.name)
      : undefined);
  const isSuperUserRole = isSuperUserRoleValue(selectedRoleId, selectedRoleName);
  const selectedRoleHidesDistributor = isSuperUserRole || isSuperAdminRoleValue(selectedRoleName);
  const visibleSteps = isSuperUserRole ? steps.slice(0, 1) : steps;
  const showVehicleLimitations = isCustomerUserRoleValue(selectedRoleId, selectedRoleName);
  const existingUser = savedUser ?? hydratedRecord;
  const existingUserValues = buildInitialFormValues(existingUser);
  const existingUserWasCustomerAccountUser = isCustomerRoleValue(
    existingUser?.userRole?.id,
    existingUser?.userRole?.name
  );
  const isAddingUserToCustomerAccount =
    isCustomerRoleValue(selectedRoleId, selectedRoleName) &&
    Boolean(watchedClientId) &&
    (!isEditing ||
      !existingUserWasCustomerAccountUser ||
      String(existingUserValues.clientId) !== String(watchedClientId));
  const usersLimitGuard = useCustomerLimitGuard({
    limitKey: "users",
    currentValue: 0,
    customerId: watchedClientId,
    enabled: open && step === 0 && isAddingUserToCustomerAccount,
  });

  useEffect(() => {
    if (!open) {
      hydratedKeyRef.current = undefined;
      return;
    }

    if (initialEditMode && !userDetailLoaded) return;

    const hydrateKey = `${initialEditMode ? "edit" : "create"}:${recordUserId ?? "new"}`;
    if (hydratedKeyRef.current === hydrateKey) return;

    const initialRecord = hydratedRecord ?? null;

    form.setFieldsValue(buildInitialFormValues(initialRecord));
    setSavedUser(initialRecord);
    setStep(0);
    setMaxReachableStep(initialEditMode ? 2 : 0);
    setPermissionsBySystemId({});
    setPermissionSystems([]);
    setSelectedSystemId(undefined);
    setEnabledSystemIds(new Set());
    setDirtyEnabledSystemIds(new Set());
    setDirtyPermissionSystemIds(new Set());
    setIsPermissionsLoading(false);
    setLimitations(defaultLimitations);
    hydratedKeyRef.current = hydrateKey;
  }, [form, hydratedRecord, initialEditMode, open, recordUserId, userDetailLoaded]);

  const handleClose = () => {
    form.resetFields();
    setStep(0);
    setMaxReachableStep(0);
    setSavedUser(null);
    setPermissionsBySystemId({});
    setPermissionSystems([]);
    setSelectedSystemId(undefined);
    setEnabledSystemIds(new Set());
    setDirtyEnabledSystemIds(new Set());
    setDirtyPermissionSystemIds(new Set());
    setIsPermissionsLoading(false);
    setLimitations(defaultLimitations);
    onClose();
  };

  const saveUserInfo = async () => {
    const values = await form.validateFields();
    const payload = buildUserPayload(values, selectedDistributor, {
      includePassword: shouldRequirePassword(isEditing, values.userRoleId),
      omitDistributor: selectedRoleHidesDistributor,
    });

    const response =
      isEditing && activeUserId
        ? await updateUserMutation.mutateAsync({ id: activeUserId, payload })
        : await createUserMutation.mutateAsync(payload);

    const user = normalizeUserDetailResponse(response)?.user ?? getResponseObject<User>(response);

    if (!user || !getUserId(user)) {
      throw new Error(t("user_save_failed"));
    }

    const savedUserInfo: User = {
      ...user,
      userRole: {
        ...(user.userRole ?? {}),
        id: values.userRoleId,
        name: getRoleName(roles, values.userRoleId) ?? user.userRole?.name,
      },
      client:
        values.clientId !== undefined && values.clientId !== null && values.clientId !== ""
          ? { ...(user.client ?? {}), id: values.clientId }
          : null,
    };

    setSavedUser(savedUserInfo);
    message.success(
      t(isEditing ? "user_management_updated_successfully" : "user_management_added_successfully")
    );

    return savedUserInfo;
  };

  const saveCurrentPermissions = async () => {
    if (!activeUserId) return;

    if (permissionSystems.length && dirtyEnabledSystemIds.size) {
      await Promise.all(
        permissionSystems
          .filter((system) => dirtyEnabledSystemIds.has(String(system.id)))
          .map((system) =>
            saveAllowedSystemMutation.mutateAsync({
              userId: activeUserId,
              systemTypeId: system.id,
              enable: enabledSystemIds.has(system.id),
            })
          )
      );
    }

    const visibleSystemIds = new Set(permissionSystems.map((system) => String(system.id)));
    const permissions = Object.entries(permissionsBySystemId).flatMap(([systemId, permissions]) =>
      visibleSystemIds.has(systemId) && dirtyPermissionSystemIds.has(systemId) ? permissions : []
    );

    if (permissions.length) {
      await saveActivitiesMutation.mutateAsync(
        buildUserActivitiesPayload(permissions, activeUserId)
      );
    }

    setDirtyEnabledSystemIds(new Set());
    setDirtyPermissionSystemIds(new Set());
  };

  const saveLimitations = async () => {
    if (!activeUserId) return;

    if (showVehicleLimitations) {
      await saveVehiclesMutation.mutateAsync({
        userId: activeUserId,
        vehicleIds: limitations.selectedVehicles,
      });
    }

    await saveTimedDataMutation.mutateAsync({
      userId: activeUserId,
      enabled: limitations.historicalEnabled,
      ...(limitations.historicalEnabled && limitations.selectedPeriod
        ? {
            timedDataPermissionId: Number(limitations.selectedPeriod),
            ...(limitations.monthsAgo ? { monthAgo: limitations.monthsAgo } : {}),
          }
        : {}),
    });
  };

  const goToStep = (nextStep: UserModalStep) => {
    if (nextStep === step) return;
    if (nextStep > maxReachableStep) return;
    setStep(nextStep);
  };

  const handlePrimaryAction = async () => {
    try {
      if (step === 0) {
        if (
          isAddingUserToCustomerAccount &&
          (usersLimitGuard.isFetching || usersLimitGuard.isLimitReached)
        ) {
          return;
        }

        const user = await saveUserInfo();
        if (isSuperUserRole) {
          handleClose();
          return;
        }

        if (getUserId(user)) {
          setMaxReachableStep((previous) => (previous < 1 ? 1 : previous));
          setStep(1);
        }
        return;
      }

      if (step === 1) {
        await saveCurrentPermissions();
        setMaxReachableStep((previous) => (previous < 2 ? 2 : previous));
        setStep(2);
        return;
      }

      await saveLimitations();
      message.success(t("user_saved_successfully"));
      handleClose();
    } catch (error) {
      const responseMessage =
        error instanceof Error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;

      message.error(responseMessage ?? t("error"));
    }
  };

  const primaryLoading =
    (initialEditMode && !userDetailLoaded) ||
    createUserMutation.isPending ||
    updateUserMutation.isPending ||
    saveAllowedSystemMutation.isPending ||
    saveActivitiesMutation.isPending ||
    isPermissionsLoading ||
    saveVehiclesMutation.isPending ||
    saveTimedDataMutation.isPending ||
    (step === 0 && isAddingUserToCustomerAccount && usersLimitGuard.isFetching);

  return (
    <CustomModal
      open={open}
      width="min(800px, calc(100vw - 32px))"
      footer={null}
      closable={false}
      centered
      destroyOnHidden
      className="user-add-edit-modal"
      onCancel={handleClose}
    >
      <div className={`user-modal-shell ${step === 0 ? "user-modal-info" : ""}`}>
        <StepHeader
          current={step}
          maxReachableStep={maxReachableStep}
          visibleSteps={visibleSteps}
          onStepClick={goToStep}
          onClose={handleClose}
        />

        <h2 className="user-modal-title">{t(initialEditMode ? "edit_user" : "add_user")}</h2>

        {initialEditMode && !userDetailLoaded ? (
          <div className="user-modal-loader">
            <Spin />
          </div>
        ) : (
          <>
            {step === 0 ? (
              <>
                <CustomerLimitGuardAlert limitGuard={usersLimitGuard} />
                <UserInfoStep
                  form={form}
                  open={open}
                  isEditing={isEditing}
                  fallbackDistributorId={selectedDistributor}
                  currentRole={currentRole}
                  customerLimitErrorMessage={
                    usersLimitGuard.isLimitReached ? usersLimitGuard.description : undefined
                  }
                  onCustomerSelected={(customerId) => {
                    void usersLimitGuard.refreshCustomerLimit(customerId);
                  }}
                />
              </>
            ) : null}

            {step === 1 ? (
              <SystemPermissionsStep
                userId={activeUserId}
                roleId={selectedRoleId}
                roleName={selectedRoleName}
                permissionsBySystemId={permissionsBySystemId}
                setPermissionsBySystemId={setPermissionsBySystemId}
                selectedSystemId={selectedSystemId}
                setSelectedSystemId={setSelectedSystemId}
                enabledSystemIds={enabledSystemIds}
                setEnabledSystemIds={setEnabledSystemIds}
                setDirtyEnabledSystemIds={setDirtyEnabledSystemIds}
                setDirtyPermissionSystemIds={setDirtyPermissionSystemIds}
                setIsPermissionsLoading={setIsPermissionsLoading}
                setSystems={setPermissionSystems}
              />
            ) : null}

            {step === 2 ? (
              <LimitationsStep
                userId={activeUserId}
                limitations={limitations}
                setLimitations={setLimitations}
                showVehicleLimitations={showVehicleLimitations}
              />
            ) : null}
          </>
        )}

        <CustomActionButtons
          className="user-modal-actions"
          onCancel={handleClose}
          onOk={() => void handlePrimaryAction()}
          loading={primaryLoading}
          disabled={
            step === 0 &&
            isAddingUserToCustomerAccount &&
            (usersLimitGuard.isFetching || usersLimitGuard.isLimitReached)
          }
          okBtnLabel={step === 2 || isSuperUserRole ? "save" : "save_and_continue"}
        />
      </div>
    </CustomModal>
  );
};

export default AddEditUserModal;
